"use client";
import type { CSSProperties, ReactNode } from "react";
import { useState } from "react";
import { DialogTrigger, Dialog as RACDialog } from "react-aria-components";
import { useBreakpoint } from "../../../hooks/useBreakpoint";
import { useColorMode } from "../../../utils/ColorModeProvider";
import { Checkbox } from "../../Checkbox";
import { Popover } from "../../Popover";
import { Select } from "../../Select";
import { Tray } from "../../Tray";
// Map style preview images (base64 encoded for reliable cross-project usage)
import { neutralDark, neutralLight, sateliteLight, streetsDark, streetsLight } from "../assets/images";
import type { LayerSpec, LayerTreeNode, LayerVisibilityPatch } from "../layers";
import type { MapType } from "../mapConfig";
import { LayersPanel } from "./LayersPanel";
import { ControlBase } from "./primitives/ControlBase";
import { ControlButton } from "./primitives/ControlButton";
import type { ControlPosition } from "./primitives/utils";

interface MapLayersControlProps {
  /** Hide redundant section titles and separate sections with subtle rules. */
  showSectionHeadings?: boolean;
  /** Controlled visibility and navigation for links into layer settings. */
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  stack?: string[];
  onStackChange?: (stack: string[]) => void;

  /** Content above map type and layer choices, shared by desktop and mobile. */
  header?: ReactNode;
  /** Position basemap choices below layers when they are secondary. */
  mapTypePosition?: "top" | "bottom";
  position?: ControlPosition;
  className?: string;
  /**
   * Custom positioning - overrides position prop
   * Example: { top: '20px', right: '20px' }
   */
  style?: CSSProperties;
  /**
   * Current map type
   */
  currentMapType?: MapType;
  /**
   * Callback when map type is changed
   */
  onMapTypeChange?: (mapType: MapType) => void;
  /**
   * Layers with legend definitions that should appear in the control
   * Only layers with a `legend` property will be shown
   */
  layers?: LayerSpec[];
  /**
   * Callback when a layer's visibility is toggled
   */
  onLayerToggle?: (layerId: string) => void;
  /**
   * Callback to clear all layer selections (hide all layers)
   */
  onClearAll?: () => void;
  /**
   * Callback when a layer's selected variant changes. Only fires for layers
   * declaring `legend.variants`.
   */
  onLayerVariantChange?: (layerId: string, variantId: string) => void;
  /**
   * Recursive layer tree. When provided, the control renders the drill-down
   * layers panel in place of the flat list. Omit for the flat list.
   */
  layerTree?: LayerTreeNode[] | undefined;
  /**
   * Apply a visibility change from the panel, given as the target state per
   * affected layer. Container toggles and single-select choices both arrive as
   * one patch. Only used alongside `layerTree`.
   */
  onVisibilityChange?: ((patch: LayerVisibilityPatch) => void) | undefined;
}

const getMapTypeOptions = (isDarkMode: boolean) => [
  {
    id: "streets" as const,
    name: "Streets",
    description: "Standard street map with traffic data",
    preview: isDarkMode ? streetsDark : streetsLight,
  },
  {
    id: "satellite" as const,
    name: "Satellite",
    description: "Aerial imagery with street labels",
    preview: sateliteLight, // Only light version available
  },
  {
    id: "neutral" as const,
    name: "Neutral",
    description: "Clean design with light/dark variants",
    preview: isDarkMode ? neutralDark : neutralLight,
  },
];

/**
 * Small visual swatch component for layer legends
 */
function Swatch({ type, color, icon }: { type: "line" | "dot" | "polygon" | "pin"; color?: string; icon?: ReactNode }) {
  if (icon) {
    return (
      <span aria-hidden="true" className="flex h-5 w-5 shrink-0 items-center justify-center text-text-primary">
        {icon}
      </span>
    );
  }
  const baseClasses = "inline-block";
  const colorStyle = color ? { backgroundColor: color } : {};

  switch (type) {
    case "dot":
      // No color-ball for point layers: with chip rings themed neutral (and
      // status shading owning color going forward), a colored dot in the
      // control would assert a mapping the map no longer draws.
      return null;
    case "line":
      return <span className={`${baseClasses} h-0.5 w-6 rounded-full`} style={colorStyle} />;
    case "polygon":
      return <span className={`${baseClasses} h-3 w-4 rounded-sm border border-border`} style={colorStyle} />;
    case "pin":
      return (
        <span
          className={`${baseClasses} h-3 w-3 rounded-full rounded-bl-none border border-border transform rotate-45`}
          style={colorStyle}
        />
      );
    default:
      return null;
  }
}

export function LayersControl({
  showSectionHeadings = true,
  isOpen: controlledOpen,
  onOpenChange,
  stack,
  onStackChange,
  header,
  mapTypePosition = "top",
  position = "bottom-left",
  className,
  style,
  currentMapType = "streets",
  onMapTypeChange,
  layers = [],
  onLayerToggle,
  onClearAll,
  onLayerVariantChange,
  layerTree,
  onVisibilityChange,
}: MapLayersControlProps) {
  const { isDarkTheme } = useColorMode();
  const { isMobile } = useBreakpoint();
  const [localOpen, setLocalOpen] = useState(false);
  const [localStack, setLocalStack] = useState<string[]>([]);
  const activeStack = stack ?? localStack;
  const showMapType = showSectionHeadings || !layerTree?.length || activeStack.length === 0;
  const handleStackChange = (next: string[]) => {
    setLocalStack(next);
    onStackChange?.(next);
  };
  const isOpen = controlledOpen ?? localOpen;
  const setOpen = (open: boolean) => {
    setLocalOpen(open);
    onOpenChange?.(open);
  };
  const mapTypeOptions = getMapTypeOptions(isDarkTheme);

  const handleMapTypeSelect = (mapType: MapType) => {
    onMapTypeChange?.(mapType);
  };

  // Filter layers that have legend definitions
  const legendLayers = layers.filter((layer) => layer.legend);

  const mapTypeContent = (
    <div>
      {showSectionHeadings && <div className="text-sm font-semibold text-text-primary mb-3">Map type</div>}
      <div className="grid grid-cols-3 gap-3">
        {mapTypeOptions.map((option) => (
          <button
            key={option.id}
            type="button"
            onClick={() => handleMapTypeSelect(option.id)}
            className={
              "flex flex-col items-center space-y-2 p-3 rounded-lg text-center " +
              "hover:bg-background-muted transition-colors border border-transparent " +
              (currentMapType === option.id ? "bg-background-muted border-border" : "hover:border-border")
            }
          >
            <div
              className={
                "w-12 h-12 rounded-lg overflow-hidden bg-background-surface " +
                (currentMapType === option.id ? "border-2 border-action-default" : "border border-border-default")
              }
            >
              <img src={option.preview} alt={`${option.name} map preview`} className="w-full h-full object-cover" />
            </div>
            <div
              className={
                "text-sm " +
                (currentMapType === option.id ? "font-bold text-text-primary" : "font-normal text-text-secondary")
              }
            >
              {option.name}
            </div>
          </button>
        ))}
      </div>
    </div>
  );

  // Content that will be rendered in both Tray and Popover
  const layersContent = (
    <div
      className={`flex flex-col ${showSectionHeadings ? "gap-6" : "h-[456px] max-h-[calc(100dvh-160px)] gap-3"} ${isMobile ? "pb-6" : ""}`}
    >
      {header && (showSectionHeadings || activeStack.length === 0) && (
        <div className={showSectionHeadings ? undefined : "shrink-0 border-b border-border-default pb-3"}>{header}</div>
      )}
      {showMapType && mapTypePosition === "top" && mapTypeContent}

      {/* Drill-down panel, when the consumer describes its layers as a tree */}
      {layerTree?.length ? (
        <div
          className={
            showSectionHeadings
              ? undefined
              : "flex min-h-0 flex-1 flex-col [&>[data-layers-panel]]:flex-1 [&_.edges-stack-nav__back]:px-0"
          }
        >
          {(showSectionHeadings || onClearAll) && (
            <div className={`flex items-center mb-3 ${showSectionHeadings ? "justify-between" : "justify-end"}`}>
              {showSectionHeadings && <div className="text-sm font-semibold text-text-primary">Map details</div>}
              {onClearAll && (
                <button
                  type="button"
                  onClick={onClearAll}
                  className="text-xs text-text-secondary hover:text-text-primary transition-colors"
                >
                  Clear all
                </button>
              )}
            </div>
          )}
          <LayersPanel
            height={showSectionHeadings ? undefined : 0}
            tree={layerTree}
            layers={layers}
            onVisibilityChange={onVisibilityChange}
            stack={activeStack}
            onStackChange={handleStackChange}
          />
        </div>
      ) : null}

      {/* Dynamic Layers Section */}
      {!layerTree?.length && legendLayers.length > 0 && (
        <div>
          {(showSectionHeadings || onClearAll) && (
            <div className={`flex items-center mb-3 ${showSectionHeadings ? "justify-between" : "justify-end"}`}>
              {showSectionHeadings && <div className="text-sm font-semibold text-text-primary">Map details</div>}
              {onClearAll && (
                <button
                  type="button"
                  onClick={onClearAll}
                  className="text-xs text-text-secondary hover:text-text-primary transition-colors"
                >
                  Clear all
                </button>
              )}
            </div>
          )}
          <div className="space-y-2">
            {legendLayers.map((layer) => {
              const isVisible = layer.visible !== false;
              const variants = layer.legend?.variants;

              return (
                <div key={layer.id} className={variants ? "space-y-1.5" : undefined}>
                  <Checkbox isSelected={isVisible} onChange={() => onLayerToggle?.(layer.id)}>
                    <div className="flex items-center gap-2">
                      {layer.legend && (
                        <Swatch type={layer.legend.swatch} color={layer.legend.color} icon={layer.legend.icon} />
                      )}
                      <span className="text-sm text-text-primary">{layer.legend?.label}</span>
                    </div>
                  </Checkbox>
                  {variants && (
                    <div className="pl-6">
                      <Select
                        aria-label={`${layer.legend?.label} mode`}
                        size="sm"
                        isDisabled={!isVisible}
                        items={variants.options.map((option) => ({ ...option, value: option.id }))}
                        renderItem={(option) => option.label}
                        selectedKey={variants.selectedId}
                        onSelectionChange={(key) => onLayerVariantChange?.(layer.id, String(key))}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
      {showMapType && mapTypePosition === "bottom" && (
        <div className={showSectionHeadings ? undefined : "shrink-0 border-t border-border-default pt-2"}>
          {mapTypeContent}
        </div>
      )}
    </div>
  );

  return (
    <ControlBase position={position} className={className} style={style}>
      <div className="relative">
        {isMobile ? (
          <>
            <ControlButton icon="Stack" ariaLabel="Map layers and settings" onPress={() => setOpen(true)} />
            <Tray isOpen={isOpen} onClose={() => setOpen(false)} title="Map layers" animationVariant="scale">
              {layersContent}
            </Tray>
          </>
        ) : (
          <DialogTrigger isOpen={isOpen} onOpenChange={setOpen}>
            <ControlButton icon="Stack" ariaLabel="Map layers and settings" />
            <Popover placement="top start">
              <RACDialog
                aria-label="Map layers and settings"
                className="p-4 min-w-80 bg-background-surface border border-border rounded-lg shadow-xl focus:outline-none focus:ring-0"
              >
                {layersContent}
              </RACDialog>
            </Popover>
          </DialogTrigger>
        )}
      </div>
    </ControlBase>
  );
}
